/**
 * The two ways to finish an insurance plan without applying, each an explicit click:
 * - "Continue without applying" when an option is in the plan and not applied for: keeps the policy in
 *   the plan, marks it "Selected — not applied for" and records completion, in one save;
 * - "Finish my plan" when no option is in the plan (an insurance-only plan built from recorded
 *   policies, or no quotes at all): records completion only.
 * Both then open Your Plan (the "Your plan is ready" arrival the first time). Neither ever removes a
 * policy. A failed save keeps the consumer where they are.
 */
import { useConsumerFlow } from './use-consumer-flow';
import { useConsumerPolicies } from './use-consumer-policies';
import { useConsumerPlan } from './use-consumer-plan';
import { PLAN_READY_STATE } from './use-plan-ready-arrival';
import { useRecordFlowCompletion } from './use-record-flow-completion';

export function useInsuranceFinish(clientId: string) {
  const { goTo } = useConsumerFlow(clientId);
  const { plan } = useConsumerPlan(clientId);
  const { addedQuote } = useConsumerPolicies(clientId);
  const { recordFlowCompletion, isRecording } = useRecordFlowCompletion(clientId);

  const finish = async (alsoSave: Partial<ConsumerPlanState> = {}) => {
    try {
      const { firstCompletion } = await recordFlowCompletion(alsoSave);
      goTo('your-plan', { state: firstCompletion ? PLAN_READY_STATE : undefined });
    } catch {
      // The plan hook has already said the save failed.
    }
  };

  return {
    canContinueWithoutApplying: !!addedQuote && addedQuote.status !== 'application-submitted',
    continueWithoutApplying: () =>
      finish({ continuedWithoutApplyingQuoteKey: plan?.selectedQuoteKey ?? null }),
    canFinishPlan: !addedQuote,
    finishPlan: () => finish(),
    isFinishing: isRecording,
  };
}
